import React, {useMemo, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {
    ConfirmModal,
    DatePicker,
    Form,
    InputLabel,
    Select,
    SelectOptionProps,
    TableLink,
    useNotifications
} from "peggirkit";
import {EmployeeTimeEntry, EmployeeTimeEntryFormValues} from "@/data/entities/employee";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {SetStateFn} from "@/utils/SetStateUtils";
import {Controller, useController, useForm} from "react-hook-form";
import EditTimeInput from "@widgets/employees/editor/timeEntries/edit/input/EditTimeInput";
import {nl} from "date-fns/locale";
import WorkedHoursSummaryFormatter from "@widgets/employees/editor/timeEntries/columns/WorkedHoursSummaryFormatter";
import dayjs from "dayjs";
import {api} from "@/data/api";

export type PickDateProps = {
    show: boolean,
    defaultDay: Date,
    onContinue: (selectedDate: Date | undefined) => void,
};

type Props = {
    entry: EmployeeTimeEntry,
    setLastUpdated: SetStateFn<Date>,
};

const EditTimeEntry = ({entry, setLastUpdated}: Props) => {
    const intl = useIntl();
    const [pickDateModal, setPickDateModal] = useState<PickDateProps>({
        show: false,
        defaultDay: new Date(),
        onContinue: () => console.log("Not yet initialized"),
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const {notify} = useNotifications();
    const [loading, setLoading] = useState(false);
    const {control, reset, handleSubmit, formState: {errors}, watch} = useForm<EmployeeTimeEntryFormValues>({
        defaultValues: {
            clockIn: entry.clockIn,
            clockOut: entry.clockOut,
            breakMinutes: entry.breakMinutes,
        },
    });
    const breakMinutesOptions: SelectOptionProps<number>[] = useMemo(() => ([
        {
            value: 0,
            displayValue: intl.formatMessage({id: "nMinutes"}, {n: 0})
        },
        {
            value: 15,
            displayValue: intl.formatMessage({id: "nMinutes"}, {n: 15})
        },
        {
            value: 30,
            displayValue: intl.formatMessage({id: "nMinutes"}, {n: 30})
        },
    ]), [intl]);
    const {mutate} = useMutation({
        mutationFn: (fields: EmployeeTimeEntryFormValues) => api.timeEntry.updateTimeEntry(entry.id, fields),
        onSuccess: () => {
            notifySuccess(intl.formatMessage({id: "saved"}), notify)
            setLastUpdated(new Date());
        },
        onError: () => {
            notifyDanger(intl.formatMessage({id: "systemError"}), notify);
            setLoading(false);
        },
    });
    const {
        field: {
            onChange: clockInOnChange,
            value: clockIn,
        },
    } = useController({control, name: "clockIn", defaultValue: entry.clockIn});
    const {
        field: {
            onChange: clockOutOnChange,
            value: clockOut,
        },
    } = useController({
        control,
        name: "clockOut",
        defaultValue: entry.clockOut,
        rules: {
            validate: (value) => {
                if (value === null || dayjs(value).isAfter(clockIn)) {
                    return true;
                }

                return intl.formatMessage({id: "clockOutMustBeBeforeClockIn"});
            },
        },
    });
    const handleSubmitHandler: (() => void) = handleSubmit((data: EmployeeTimeEntryFormValues) => {
        setLoading(true);
        mutate(data)
    });

    return (
        <>
            <TableLink
                buttonAction={{
                    as: "button",
                    actionProps: {
                        onClick: () => {
                            reset();
                            setShowEditModal(true);
                        },
                    },
                }}
            >
                <FormattedMessage id={"edit"}/>
            </TableLink>

            <ConfirmModal
                type={"info"}
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                title={intl.formatMessage({id: "editTimeRegistration"})}
                dismissButton={{
                    text: intl.formatMessage({id: "cancel"}),
                }}
                continueButton={{
                    text: intl.formatMessage({id: "save"}),
                    onClick: () => handleSubmitHandler(),
                    props: {color: "primary"},
                }}
                disabled={loading}
                loading={loading}
                position={"center-bottom"}
                breakpoints={{md: {position: "center"}}}
            >
                <Form className={"py-8"}>
                    <EditTimeInput
                        label={intl.formatMessage({id: "clockedInOn2"})}
                        pickDateLabel={intl.formatMessage({id: "pickStartDate"})}
                        value={clockIn}
                        onChange={clockInOnChange}
                        setPickDateModal={setPickDateModal}
                        setShowEditModal={setShowEditModal}
                    />

                    <EditTimeInput
                        label={intl.formatMessage({id: "clockedOutOn"})}
                        pickDateLabel={intl.formatMessage({id: "pickEndDate"})}
                        value={clockOut}
                        onChange={clockOutOnChange}
                        setPickDateModal={setPickDateModal}
                        setShowEditModal={setShowEditModal}
                    />
                    {
                        errors.clockOut &&
                        <p className={"-mt-6 text-red-600 dark:text-red-400"}>
                            {errors.clockOut.message}
                        </p>
                    }

                    <Controller
                        control={control}
                        name={"breakMinutes"}
                        render={({field: {value, onChange}}) => {
                            const selectValue = breakMinutesOptions.filter(x => x.value === value)
                            return (
                                <Select<number>
                                    label={<InputLabel><FormattedMessage id={"paused"}/></InputLabel>}
                                    options={breakMinutesOptions}
                                    value={selectValue.length > 0 ? selectValue[0] : null}
                                    displayValueEmpty={"-"}
                                    onChange={(selectedOption: SelectOptionProps<number>) => {
                                        onChange(selectedOption.value)
                                    }}
                                />
                            );
                        }}
                    />

                    <div className={"flex flex-col items-start justify-start gap-1"}>
                        <InputLabel>
                            <FormattedMessage id={"workedHours"}/>
                        </InputLabel>
                        <WorkedHoursSummaryFormatter
                            timeEntry={{
                                id: entry.id,
                                clockIn: watch("clockIn"),
                                clockOut: watch("clockOut"),
                                breakMinutes: watch("breakMinutes"),
                            }}
                        />
                    </div>
                </Form>
            </ConfirmModal>

            <DatePicker
                key={pickDateModal.defaultDay.toISOString()}
                title={intl.formatMessage({id: "pickDate"})}
                show={pickDateModal.show}
                setShow={(show: boolean) => {
                    setPickDateModal((prev) => ({...prev, show}))
                    setShowEditModal(!show);
                }}
                pickerConfig={{
                    type: "single",
                    defaultDay: pickDateModal.defaultDay,
                    onContinue: pickDateModal.onContinue,
                }}
                calendarLocale={nl}
                cancelButton={{text: intl.formatMessage({id: "cancel"})}}
                continueButton={{text: intl.formatMessage({id: "continue"})}}
                withTimeConfig={{enabled: false}}
                horizontalViewBreakpoint={"xl"}
            />
        </>
    );
};

export default EditTimeEntry;